import React, { useEffect, useState } from "react";
import AssignedParticipant from "../../../interfaces/participant/AssignedParticipant";
import Select, { SingleValue } from "react-select";
import { useAuth } from "../../../firebase/AuthProvider";
import TaskCreationLKValues from "../../../interfaces/lkValues/TaskCreationLKValues";
import { getTaskLKValues } from "../../../services/taskServices/TaskLKService";
import { reassigneParticipant } from "../../../services/participantServices/ParticipantService";
import Banner from "../../ui/Banner/Banner";
import LoadingSpinner from "../../ui/Loading/LoadingSpinner";
import { Button, Modal } from "react-bootstrap";
import { ActiveParticipantDTO } from "../../../interfaces/Participant";

interface TransferParticipantComponentProps {
  participants: AssignedParticipant[] | null | undefined | ActiveParticipantDTO[];
  isOpen: boolean; // changed prop name to match EmailModal
  onClose: () => void;
}

interface OptionType {
  value: string;
  label: string;
}

const TransferParticipantComponent: React.FC<TransferParticipantComponentProps> = ({
  participants,
  isOpen,
  onClose,
}) => {
  const { currentUser } = useAuth();
  const [participantOptions, setParticipantOptions] = useState<OptionType[]>(
    []
  );
  const [selectedParticipant, setSelectedParticipant] = useState<string>();
  const [assigneeOptions, setAssigneeOptions] = useState<OptionType[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (participants !== null && participants !== undefined) {
      const options = participants.map((participant) => ({
        value: participant.id.toString(),
        label: participant.firstName + " " + participant.lastName,
      }));
      setParticipantOptions(options);
    }
  }, [participants]);

  useEffect(() => {
    const fetchLkValues = async () => {
      if (currentUser) {
        const authToken: string = await currentUser.getIdToken();
        const fetchedValues: TaskCreationLKValues | null =
          await getTaskLKValues(authToken);
        if (fetchedValues) {
          setAssigneeOptions(
            fetchedValues.users.map((user) => ({
              value: user.id.toString(),
              label: user.firstName + " " + user.lastName,
            }))
          );
        }
      }
    };
    fetchLkValues();
  }, [currentUser]);

  const handleSelectedParticipantChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const participant = selectedOption ? selectedOption.value : "";
    setSelectedParticipant(participant);
  };

  const handleSelectedAssigneeChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const assignee = selectedOption ? selectedOption.value : "";
    setSelectedAssignee(assignee);
  };

  const transferParticipant = async () => {
    setIsLoading(true);
    const participantName = `${
      participantOptions?.filter((p) => p.value === selectedParticipant)[0]
        .label
    }`;
    if (currentUser !== null && selectedParticipant && selectedAssignee) {
      const authToken = await currentUser.getIdToken();
      reassigneParticipant(selectedParticipant, selectedAssignee, authToken)
        .then((res) => {
          setSuccessMessage(
            `Successfully transferred participant ${participantName}`
          );
          setSelectedAssignee(undefined);
          setSelectedParticipant(undefined);
        })
        .catch((err) => {
          console.log(err);
          setErrorMessage(err?.message || "Error processing request.");
        });
    }

    setIsLoading(false);
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered className="custom_popup">
      <Modal.Header closeButton>
        <Modal.Title>Transfer Participant</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-visible">
        {errorMessage !== "" && (
          <Banner
            dismissBanner={() => setErrorMessage("")}
            message={errorMessage}
            status="error"
          />
        )}
        {successMessage !== "" && (
          <Banner
            dismissBanner={() => setSuccessMessage("")}
            message={successMessage}
            status="success"
          />
        )}
        <div className="custom_input_flled d-block w-auto">
          <label>Which participant are you wanting to transfer?</label>
          <Select
            id="participants"
            name="participants"
            value={participantOptions.find(
              (option) => option.value === selectedParticipant
            )}
            onChange={handleSelectedParticipantChange}
            options={participantOptions}
            placeholder={"Choose who to assign to..."}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        {selectedParticipant && (
          <div className="custom_input_flled d-block w-auto">
            <label>Who do you want to transfer this participant to?</label>
            <Select
              id="participants"
              name="participants"
              value={assigneeOptions.find(
                (option) => option.value === selectedAssignee
              )}
              onChange={handleSelectedAssigneeChange}
              options={assigneeOptions}
              placeholder={"Choose who to assign to..."}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        )}
        {selectedParticipant && selectedAssignee && (
          <div className="transfer_participant_txt">
            <p>
              Transferring
              <span>
                {
                  participantOptions.filter(
                    (participant) => participant.value === selectedParticipant
                  )[0].label
                }
              </span>
              will result in all of their assigned tasks and drive files being
              moved to
              <span>
                {
                  assigneeOptions.filter(
                    (assignee) => assignee.value === selectedAssignee
                  )[0].label
                }
              </span>
              's drive. Are you sure you want to continue?
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {selectedParticipant && selectedAssignee && (
          <Button
            variant="primary"
            onClick={transferParticipant}
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner /> : "Transfer"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TransferParticipantComponent;
